import {
  FetchArgs,
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { getIdToken } from "@firebase/auth";
import { auth } from "../firebase/firebase";
import { captureException, addBreadcrumb } from "../sentry.config";
import { getSession } from "./auth";
import { updateOnlineStatus } from "./staticSlice";
import { toast } from "../components/Toast/toast";

interface CustomFetchBaseQueryArgs extends FetchArgs {
  customRedirect?: boolean;
}

const baseQuery = fetchBaseQuery({
  // baseUrl: "https://oogwai.gaggle.online/",
  // baseUrl: "https://analytics.oogw.ai/",
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: async (headers) => {
    // Public routes that don't require authentication
    const publicRoutes = [
      "/login",
      "/signup",
      "/verify-email",
      "/enter-referral-code",
      "/about-your-company",
      // "/integration-pereference",
      // "/select-goals",
      // "/setup-workspace",
      "/set-password",
      "/forgot-password",
    ];

    if (!publicRoutes.includes(window.location.pathname)) {
      try {

        const urlParams = new URLSearchParams(window.location.search);
        const emailShop = urlParams.get("email");
        const getShopifyUser = localStorage.getItem("oogw.ai_user");
        const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";

        if (emailShop || tokenType === "Shopify") {
          headers.set("Authorization", `${tokenType} ${emailShop || getShopifyUser}`);

        } else {
          const session: any = await getSession();
          const token = session?.idToken?.jwtToken;
          const getEmail = localStorage.getItem("oogw.ai_shadow_user_email");
          const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";
          if (getEmail && getEmail !== 'undefined') {
            headers.set("email", getEmail);
          }
          if (token) {
            headers.set("Authorization", `${tokenType} ${token}`);
          } else {
            throw new Error("No token available in session");
          }

        }


      } catch (error) {
        // console.error("Token fetch error:", error);
        addBreadcrumb({
          message: 'Token fetch failed',
          category: 'auth',
          level: 'error',
          data: { error: String(error) },
        });
        captureException(error, { context: 'token_fetch' });
        toast.error("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return headers;
  }
});


// Custom fetch base query with token handling
const customFetchBaseQuery: BaseQueryFn<
  string | CustomFetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> = async (args: any, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Check if response is HTML instead of JSON
  if (result?.data && typeof result.data === 'string') {
    // Check if the response looks like HTML
    const isHTML = /<[^>]+>/.test(result.data as string);
    if (isHTML) {
      // console.error('Received HTML response instead of JSON:', result.data);
      addBreadcrumb({
        message: 'Received HTML response instead of JSON',
        category: 'api',
        level: 'error',
        data: {
          url: typeof args === 'string' ? args : args.url,
          responseSnippet: (result.data as string).substring(0, 200),
        },
      });
      captureException(new Error('HTML response instead of JSON'), {
        url: typeof args === 'string' ? args : args.url,
        response: (result.data as string).substring(0, 500),
      });
      toast.error("Something went wrong. Please try again.", {
        toastId: "html-response-error",
      });
      // Convert to an error result
      return {
        error: {
          status: 'PARSING_ERROR',
          error: 'Received HTML response instead of JSON',
          data: result.data
        } as FetchBaseQueryError
      };
    }
  }

  if (result?.error?.status === 401) {
    // console.warn("401 Unauthorized - attempting to refresh session");
    addBreadcrumb({
      message: '401 Unauthorized - attempting token refresh',
      category: 'auth',
      level: 'warning',
      data: {
        url: typeof args === 'string' ? args : args.url,
      },
    });

    try {
      const session: any = await getSession(); // this may already refresh session internally
      const token = session?.idToken?.jwtToken;

      if (token) {
        const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";
        // force-set the token in headers for retry
        if (typeof args === "object") {
          args.headers = {
            ...(args.headers || {}),
            Authorization: `${tokenType} ${token}`,
          };
        }

        const retryResult = await baseQuery(args, api, extraOptions);
        return retryResult;
      }
    } catch (error) {

      const getShopifyUser = localStorage.getItem("oogw.ai_user");
      const tokenType = localStorage.getItem("oogw.ai_user_type") || "Bearer";
      if (getShopifyUser) {
        args.headers = {
          ...(args.headers || {}),
          Authorization: `${tokenType} ${getShopifyUser}`,
        };
      } else {
        // console.error("Failed to refresh session after 401:", error);
        addBreadcrumb({
          message: 'Session refresh failed after 401',
          category: 'auth',
          level: 'error',
          data: { error: String(error) },
        });
        captureException(error, { context: 'session_refresh_401' });
        localStorage.clear();
        toast.error("Session expired. Please log in again.", {
          toastId: "session-expired",
        });
        window.location.href = "/login";
      }

    }
  }

  // Handle fetch errors and capture via Sentry
  if (result?.error) {
    // Check for parsing errors (including HTML responses)
    if (result.error.status === 'PARSING_ERROR') {
      // console.error('Parsing error:', result.error);
      toast.error("Something went wrong. Please try again.", {
        toastId: "parsing-error",
      });
    }

    addBreadcrumb({
      message: 'API request failed',
      category: 'api',
      level: 'error',
      data: {
        url: typeof args === 'string' ? args : args.url,
        status: result.error.status,
        error: result.error,
      },
    });
    captureException(result?.error, {
      url: typeof args === 'string' ? args : args.url,
      status: result.error.status,
    });
    if (
      result.error.status === "FETCH_ERROR" && !navigator.onLine
    ) {
      api.dispatch(updateOnlineStatus(false));
      // Optionally redirect to an error page
      // window.location.href = "/error";
    } else if (result.error.status === "FETCH_ERROR" && navigator.onLine) {
      api.dispatch(updateOnlineStatus(true));
    }
  }

  return result;
};

// API Slice
export const baseApi = createApi({
  reducerPath: "baseApiReducer",
  baseQuery: customFetchBaseQuery,
  endpoints: () => ({}),
  tagTypes: [
    "generalSettings",
    "notificationSettings",
    "metaIntegration",
    "teams",
    "billing",
    "dashboard",
    "useHistory",
    "conversationHistory",
    "createProfile",
    "currentPlan",
    "customIntegration",
    "integrationList",
    "integrationStatus",
    "defaultProperties",
    "workspace-integration",
    "integration-status",
  ],
});
